import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  LpiFilters,
  LpiCoverage,
  LpiAggregateBucket,
  LpiBucketsResponse,
} from './types';

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

export interface LpiAggregatesState {
  coverage: LpiCoverage | null;
  monthly: LpiAggregateBucket[] | null;
  byVehicle: LpiAggregateBucket[] | null;
  byClient: LpiAggregateBucket[] | null;
  byOrder: LpiAggregateBucket[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Same field order and omit-empty rules as LpiReports.buildQueryString. */
const buildFilterQuery = (filters: LpiFilters): string => {
  const params = new URLSearchParams();
  if (filters.client_id.length > 0)
    params.append('client_id', filters.client_id.join(','));
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.vehicle_id.length > 0)
    params.append('vehicle_id', filters.vehicle_id.join(','));
  if (filters.vehicle_type.length > 0)
    params.append('vehicle_type', filters.vehicle_type.join(','));
  if (filters.carrier_id.length > 0)
    params.append('carrier_id', filters.carrier_id.join(','));
  // Without this the charts would keep showing every order after a
  // transport-order drill-through, disagreeing with the table beneath them.
  if (filters.transport_order_number.length > 0)
    params.append(
      'transport_order_number',
      filters.transport_order_number.join(',')
    );
  return params.toString();
};

export function useLpiAggregates(
  filters: LpiFilters,
  getSessionTokens: () => Promise<SessionTokens>,
  enabled: boolean
): LpiAggregatesState {
  const [coverage, setCoverage] = useState<LpiCoverage | null>(null);
  const [monthly, setMonthly] = useState<LpiAggregateBucket[] | null>(null);
  const [byVehicle, setByVehicle] = useState<LpiAggregateBucket[] | null>(null);
  const [byClient, setByClient] = useState<LpiAggregateBucket[] | null>(null);
  const [byOrder, setByOrder] = useState<LpiAggregateBucket[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  // Serialise the filters so the effect re-runs on value change, not identity.
  const query = useMemo(() => buildFilterQuery(filters), [filters]);

  // Callers often pass an inline closure (e.g.
  // `() => getSessionTokens(getSession)`), which is a new reference on every
  // render. Holding the latest one in a ref lets the effect read it without
  // depending on it, so a caller's un-memoized closure can't trigger a
  // refetch of all three endpoints on every unrelated re-render. Do not
  // "simplify" this back to a plain dependency-array entry.
  const getSessionTokensRef = useRef(getSessionTokens);
  useEffect(() => {
    getSessionTokensRef.current = getSessionTokens;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { jwtToken, sessionId } = await getSessionTokensRef.current();
        const base = process.env.REACT_APP_LPI_API_URL;
        const opts = {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
          validateStatus: (status: number) =>
            (status >= 200 && status < 300) || status === 304,
        };

        const [cov, mon, veh, cli, ord] = await Promise.all([
          axios.get<LpiCoverage>(
            `${base}/lpi/aggregate/coverage?${query}`,
            opts
          ),
          axios.get<LpiBucketsResponse>(
            `${base}/lpi/aggregate/monthly?${query}`,
            opts
          ),
          axios.get<LpiBucketsResponse>(
            `${base}/lpi/aggregate/by-vehicle?${query}`,
            opts
          ),
          axios.get<LpiBucketsResponse>(
            `${base}/lpi/aggregate/by-client?${query}`,
            opts
          ),
          axios.get<LpiBucketsResponse>(
            `${base}/lpi/aggregate/by-order?${query}`,
            opts
          ),
        ]);

        if (cancelled) return;

        setCoverage(cov.data ?? null);
        setMonthly(mon.data?.buckets ?? []);
        setByVehicle(veh.data?.buckets ?? []);
        setByClient(cli.data?.buckets ?? []);
        setByOrder(ord.data?.buckets ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error('Failed to load aggregates'));
        setCoverage(null);
        setMonthly(null);
        setByVehicle(null);
        setByClient(null);
        setByOrder(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled, query, attempt]);

  return {
    coverage,
    monthly,
    byVehicle,
    byClient,
    byOrder,
    isLoading,
    error,
    refetch,
  };
}
